import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UsersService } from '../../../services/users-service.service';
import { ProfileDialogService } from '../../../services/profile-dialog.service';
import { AccountBlockService } from '../../../services/account-block.service';
import { PersonLinkComponent } from '../../../components/shared/person-link.component';
import { EventLinkComponent } from '../../../components/shared/event-link.component';
import {
  AbuseCaseDetailDto,
  AppealDto,
} from '../../../data-transfer-objects/abuse-case.dto';
import { FeedPostDto, feedPostSummary } from '../../../data-transfer-objects/feed-post.dto';
import { FeedPostListComponent } from '../../../components/shared/feed-post-list.component';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../action-confirm-dialog/action-confirm-dialog.component';

/**
 * One account's case: the evidence, then the actions.
 *
 * This is where the alert emails land, so it has to answer "is this really a
 * scammer" before it offers a way to act. Hence the opener, the recipients and
 * the reports above the buttons rather than beside them.
 *
 * Every action below opens the shared confirm dialog before doing anything -
 * nothing here fires from a single click. For the three that email somebody
 * (Block, Restore, Uphold) the dialog shows exactly what will be sent and lets
 * it be edited first, so whoever clicks Confirm is answerable for the words
 * that actually go out, not for a template they never read.
 *
 * Deleting the chats stays on its own gate, typing the word rather than a
 * dialog: it is the one action here with no possible undo, and a dialog with
 * a Confirm button sits in the same place the first click did, which is no
 * extra protection at all for that one.
 */
@Component({
  selector: 'app-abuse-case',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    PersonLinkComponent,
    EventLinkComponent,
    FeedPostListComponent,
  ],
  templateUrl: './abuse-case.component.html',
  styleUrls: ['./abuse-case.component.scss'],
})
export class AbuseCaseComponent implements OnInit {
  uid = '';
  case: AbuseCaseDetailDto | null = null;
  loading = true;
  error: string | null = null;
  notice: string | null = null;
  busy: string | null = null;

  /** Typed into the purge confirmation before the button will fire. */
  purgeConfirmation = '';
  purging = false;
  purgeProgress: { chats: number; messages: number } | null = null;
  loadingProfile = false;

  constructor(
    private usersService: UsersService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private profileDialog: ProfileDialogService,
    private accountBlock: AccountBlockService,
  ) {}

  ngOnInit(): void {
    this.uid = this.route.snapshot.paramMap.get('uid') ?? '';
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.usersService.getAbuseCase(this.uid).subscribe({
      next: (c) => {
        /* An API that predates the block panel sends no `blockedBy` at all,
         * which happens for real whenever the site deploys ahead of the
         * functions. Saying the read failed is both true and the safe thing to
         * render - the alternative is a page that throws, or one that quietly
         * claims nobody blocked them. */
        this.case = { ...c, blockedBy: c.blockedBy ?? { blockers: [], known: false } };
        this.loading = false;
      },
      error: (err) => {
        this.error =
          err?.status === 404
            ? `No abuse case for ${this.uid}. The account may never have been flagged.`
            : `Could not load the case: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  /*
   * Block and Unblock live in AccountBlockService, shared with the profile
   * dialog: the same confirmation, the same reviewed email, the same reason
   * field wherever an admin meets the account. busy is held from the click
   * through the dialog, so a second action cannot stack on top of an open
   * one; the service only completes once the dialog has closed.
   */
  block(): void {
    this.runShared('block', this.accountBlock.block(this.uid), 'Account blocked.');
  }

  unblock(): void {
    this.runShared('unblock', this.accountBlock.unblock(this.uid), 'Account restored.');
  }

  /** One row of the account's life, oldest first. */
  get timeline(): { at: string; what: string }[] {
    const c = this.case;
    if (!c) return [];
    const rows: { at: string; what: string }[] = [];
    const add = (at: string | null | undefined, what: string) => {
      if (at) rows.push({ at, what });
    };
    add(c.profile?.createdAt, 'Signed up');
    add(c.profile?.lastSeen, 'Last seen in the app');
    add(c.flaggedAt, 'Flagged by the guard');
    add(c.bannedAt, `Banned (${c.banSource ?? 'unknown source'})`);
    (c.appeals ?? []).forEach((a) => {
      add(a.createdAt, 'Appealed');
      if (a.status !== 'open') {
        add(a.decidedAt, a.status === 'restored' ? 'Restored' : 'Appeal upheld');
      }
    });
    add(c.chatsPurgedAt, 'Chats archived and deleted');
    return rows.sort((a, b) => a.at.localeCompare(b.at));
  }

  /**
   * How the account's whole life reads, not the guard's 24 hours.
   *
   * "0 chats" over ten months and "0 chats" today are the same words on the
   * old panel and completely different facts. This one says which.
   */
  get lifetimeSummary(): string {
    const l = this.case?.lifetime;
    if (!l || l.chats < 0) return 'could not be counted';
    if (!l.chats) return 'never messaged anyone';
    const replied = `${l.replied} of ${l.countedChats} wrote back`;
    return l.capped
      ? `${l.chats} conversations ever · ${replied} (first ${l.countedChats} counted)`
      : `${l.chats} conversations ever · ${replied}`;
  }

  /** True when the account has existed a while and never once messaged anybody. */
  get dormantSinceSignup(): boolean {
    const l = this.case?.lifetime;
    const created = this.case?.profile?.createdAt;
    if (!l || l.chats !== 0 || !created) return false;
    const days = (Date.now() - new Date(created).getTime()) / 86_400_000;
    return days > 30;
  }

  /** Open the same profile dialog the users list opens. */
  openProfile(): void {
    if (this.loadingProfile) return;
    this.loadingProfile = true;
    this.error = null;
    this.profileDialog
      .open(this.uid)
      .catch((err: Error) => (this.error = err.message))
      .finally(() => (this.loadingProfile = false));
  }

  /** The appeal waiting on an answer, when there is one. */
  get openAppeal(): AppealDto | null {
    return this.case?.appeals?.find((a) => a.status === 'open') ?? null;
  }

  /** Appeals already answered, newest first, for context on a repeat writer. */
  get pastAppeals(): AppealDto[] {
    return (this.case?.appeals ?? []).filter((a) => a.status !== 'open');
  }

  /**
   * How the reply count reads.
   *
   * Deliberately words rather than a score: "9 written to, 0 replied" is a
   * fact, and a number out of ten would invite trusting it over the case.
   */
  get replySummary(): string {
    const r = this.case?.replies;
    if (!r || !r.known) return 'could not be counted';
    if (!r.chats) return 'no chats in the guard window';
    return `${r.replied} of ${r.chats} wrote back`;
  }

  /** True when nobody replied to anybody - the strongest single signal here. */
  get noneReplied(): boolean {
    const r = this.case?.replies;
    return Boolean(r?.known && r.chats > 0 && r.replied === 0);
  }

  /** True when any of these chats were with members of this account's events. */
  get hasPeerChats(): boolean {
    return (this.case?.peerChatCount ?? 0) > 0;
  }

  /**
   * Whether the people reached were strangers or fellow attendees.
   *
   * The single most useful line on this page for an account flagged during an
   * event week. Somebody introducing themselves to the other members of a
   * conference they are attending looks identical to a blast by volume and by
   * repetition, and reads as completely ordinary the moment you know who they
   * were writing to.
   */
  get peerSummary(): string {
    const c = this.case;
    if (!c || !c.peerChatCount) return '';
    const shared = c.sharedEvents?.length
      ? ` (latest: ${c.sharedEvents.join(', ')})`
      : '';
    return `${c.peerChatCount} of ${c.chatCount} went to members of their own events${shared}.`;
  }

  /** True when every chat in the window was with a fellow event member. */
  get allPeerChats(): boolean {
    const c = this.case;
    return Boolean(c && c.chatCount > 0 && c.peerChatCount === c.chatCount);
  }

  restoreAppeal(): void {
    this.previewThenDecide(
      'restore',
      'Restore this account and reply?',
      'Re-enables sign-in, clears the guard\'s tracking window, removes the name ' +
        'from the persona watchlist, marks this appeal as restored, and sends the ' +
        'email below. All four happen together — there is no way to restore ' +
        'without the person being told.',
      'Restore & send',
      false,
      'Account restored and the person told.',
    );
  }

  upholdAppeal(): void {
    this.previewThenDecide(
      'uphold',
      'Uphold the ban and reply?',
      'Marks this appeal as upheld — the account stays banned — and sends the ' +
        'email below telling them the decision stands. The appeal cannot be ' +
        'reopened after this.',
      'Uphold & send',
      true,
      'Appeal upheld and the person told.',
    );
  }

  private previewThenDecide(
    decision: 'restore' | 'uphold',
    title: string,
    description: string,
    confirmLabel: string,
    danger: boolean,
    done: string,
  ): void {
    const appeal = this.openAppeal;
    if (!appeal || this.busy) return;
    this.busy = decision;
    this.error = null;
    this.usersService.getAppealDecisionPreview(appeal.id, decision).subscribe({
      next: (preview) => {
        // busy stays set through the dialog - see the note in block().
        this.confirmThen(
          { title, description, confirmLabel, danger, emailPreview: preview },
          (email) => this.decide(decision, done, email),
        );
      },
      error: (err) => {
        this.busy = null;
        this.error = `Could not load the email preview: ${err?.message ?? err}`;
      },
    });
  }

  private decide(
    decision: 'restore' | 'uphold',
    done: string,
    email?: { subject: string; text: string },
  ): void {
    const appeal = this.openAppeal;
    if (!appeal || this.busy) return;
    this.busy = decision;
    this.error = null;
    this.usersService.decideAppeal(appeal.id, decision, email).subscribe({
      next: (result: any) => {
        this.busy = null;
        // Says whether the mail actually left, because the decision stands
        // either way and a silent restore is the failure this exists to stop.
        this.notice =
          result?.emailed === false
            ? `${done.replace(' and the person told.', '.')} The email did NOT send — write to them.`
            : done;
        this.load();
      },
      error: (err: any) => {
        this.busy = null;
        this.error =
          err?.error?.error ?? `Could not answer the appeal: ${err?.message ?? err}`;
      },
    });
  }

  deletePost(p: FeedPostDto): void {
    if (this.busy) return;
    this.busy = 'delete-post';
    this.confirmThen(
      {
        title: 'Delete this post from the feed?',
        description:
          `${feedPostSummary(p)}. Seen by ${p.viewedCount} member${p.viewedCount === 1 ? '' : 's'}. ` +
          'The post, its likes and its images are removed for everyone and cannot be ' +
          'restored from here. The account itself is untouched — use Block for that. ' +
          'No email is sent.',
        confirmLabel: 'Delete post',
        danger: true,
      },
      () => this.run('delete-post', this.usersService.deletePost(p.id), 'Post deleted.'),
    );
  }

  dismiss(): void {
    if (this.busy) return;
    this.busy = 'dismiss';
    this.confirmThen(
      {
        title: 'Dismiss as false positive?',
        description:
          "Clears the flag and resets the guard's tracking window, so this account's " +
          'next message is judged fresh rather than immediately re-tripping the same ' +
          'threshold. This does not lift a ban — use Unblock account for that. No ' +
          'email is sent.',
        confirmLabel: 'Dismiss',
      },
      () =>
        this.run(
          'dismiss',
          this.usersService.dismissAbuseCase(this.uid),
          'Flag cleared and the guard window reset.',
        ),
    );
  }

  /**
   * Open the shared confirm dialog, and run the action only if it was
   * confirmed.
   *
   * The dialog makes no calls of its own - it is handed data already loaded
   * and hands back nothing but the confirmation and, when the action emails
   * somebody, whatever the admin left in the subject and body fields. What
   * happens next is entirely this component's decision.
   * @param {ActionConfirmData} data what the dialog should show
   * @param {Function} onConfirm called with the edited email, if there is one
   */
  private confirmThen(
    data: ActionConfirmData,
    onConfirm: (email?: { subject: string; text: string }) => void,
  ): void {
    this.dialog
      .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(
        ActionConfirmDialogComponent,
        { data, autoFocus: false },
      )
      .afterClosed()
      .subscribe((result) => {
        /* Cleared before the action runs, not only on cancel.
         *
         * busy is held from the click through the dialog so a second action
         * cannot stack on top of an open one - but the actions themselves
         * start with `if (this.busy) return`, so leaving it set here meant
         * Confirm hit that guard and did nothing at all: no request, no
         * email, and a button stuck reading "Working..." for good. Whichever
         * way the dialog closed, it is finished, and the action re-sets busy
         * for its own request. */
        this.busy = null;
        if (result?.confirmed) onConfirm(result.email);
      });
  }

  /** Whether the typed confirmation matches, case-insensitively. */
  get canPurge(): boolean {
    return this.purgeConfirmation.trim().toLowerCase() === 'delete';
  }

  onConfirmationInput(event: Event): void {
    this.purgeConfirmation = (event.target as HTMLInputElement).value;
  }

  /**
   * Archive and delete the account's chats, one page at a time.
   *
   * The endpoint handles a bounded number of chats per call so it cannot run
   * past the function timeout, so this keeps calling while there is more, and
   * shows the running total rather than a spinner that says nothing.
   */
  purgeChats(): void {
    if (!this.canPurge || this.purging) return;
    this.purging = true;
    this.error = null;
    this.purgeProgress = { chats: 0, messages: 0 };

    const step = (): void => {
      this.usersService.purgeAbuseChats(this.uid).subscribe({
        next: (result) => {
          this.purgeProgress = {
            chats: (this.purgeProgress?.chats ?? 0) + result.chatsProcessed,
            messages: (this.purgeProgress?.messages ?? 0) + result.messagesArchived,
          };
          if (!result.done) {
            step();
            return;
          }
          this.purging = false;
          this.purgeConfirmation = '';
          this.notice =
            `Archived and deleted ${this.purgeProgress.chats} chats ` +
            `(${this.purgeProgress.messages} messages).` +
            (result.archivePath ? ` Archive: ${result.archivePath}` : '');
          this.load();
        },
        error: (err) => {
          this.purging = false;
          this.error = `Purge stopped: ${err?.error?.error ?? err?.message ?? err}`;
        },
      });
    };

    step();
  }

  /** run(), for an action that may have been cancelled at its confirm dialog. */
  private runShared(name: string, call: Observable<boolean>, done: string): void {
    if (this.busy) return;
    this.busy = name;
    this.error = null;
    call.subscribe({
      next: (ran) => {
        this.busy = null;
        if (!ran) return;
        this.notice = done;
        this.load();
      },
      error: (err: any) => {
        this.busy = null;
        this.error = `Could not ${name}: ${err?.message ?? err}`;
      },
    });
  }

  private run(name: string, call: any, done: string): void {
    this.busy = name;
    this.error = null;
    call.subscribe({
      next: () => {
        this.busy = null;
        this.notice = done;
        this.load();
      },
      error: (err: any) => {
        this.busy = null;
        this.error = `Could not ${name}: ${err?.message ?? err}`;
      },
    });
  }
}
